// 商品抽獎 截止時間
'use client';
import { useState, useEffect } from 'react';
import dayjs from '@/common/dayjs';
import { type Dayjs } from 'dayjs';

interface Props {
    item: any;
}

interface TimeLeft {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

// 依「現在時間」與「截止時間」即時計算剩餘，切回分頁時也會是準確的
// 傳入 from 時，改計算 from 與 end 的固定時長（用於尚未開始倒數前的靜態顯示）
const getTimeLeft = (end: Dayjs, from?: Dayjs): TimeLeft => {
    const diff = Math.max(end.diff(from ?? dayjs(), 'second'), 0);
    return {
        days: Math.floor(diff / 86400),
        hours: Math.floor((diff % 86400) / 3600),
        minutes: Math.floor((diff % 3600) / 60),
        seconds: diff % 60,
    };
};

// 補零
const pad = (n: number) => String(n).padStart(2, '0');

const DrawItemCountdown = ({ item }: Props) => {
    const { beginTime = '', endTime = '' } = item || {};

    const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

    useEffect(() => {
        // 尚未串接資料：先用 dayjs() 往後加幾天當示意（在 client 才決定，目標時間固定）
        const target = endTime ? dayjs(endTime) : dayjs().add(4, 'day');
        const begin = beginTime ? dayjs(beginTime) : null;

        // 每次都以真實時間重新計算，不做遞減累加，避免分頁被節流後產生誤差
        // 未到 beginTime 前不倒數，固定顯示 begin ~ end 的總時長
        const tick = () => {
            if (begin && dayjs().isBefore(begin)) {
                setTimeLeft(getTimeLeft(target, begin));
            } else {
                setTimeLeft(getTimeLeft(target));
            }
        };

        tick();
        const timer = setInterval(tick, 1000);

        // 切回分頁時立即校正
        const onVisible = () => {
            if (document.visibilityState === 'visible') tick();
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [beginTime, endTime]);

    return (
        <div className="flex items-center gap-x-3 xs:gap-x-6">
            <span className="hidden text-btn2 text-black4 w-8 xxs:block">截止計時</span>

            <div
                className="flex items-center gap-x-3 text-plum text-h2
                [&_span]:text-bd3n [&_span]:text-black4 [&_p]:tabular-nums [&>p>span]:pl-2"
            >
                <p>
                    {pad(timeLeft?.days ?? 0)}
                    <span>天</span>
                </p>

                <span className="w-px h-2.5 opacity-30 border-l border-black2" />

                <p>
                    {pad(timeLeft?.hours ?? 0)}
                    <span>時</span>
                </p>

                <span className="w-px h-2.5 opacity-30 border-l border-black2" />

                <p>
                    {pad(timeLeft?.minutes ?? 0)}
                    <span>分</span>
                </p>

                <span className="w-px h-2.5 opacity-30 border-l border-black2" />

                <p>
                    {pad(timeLeft?.seconds ?? 0)}
                    <span>秒</span>
                </p>
            </div>
        </div>
    );
};

export default DrawItemCountdown;
